import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/http/api'
import { Button } from '@univdiam/ui/components/button'
import { Input } from '@univdiam/ui/components/input'
import { toast } from 'sonner'
import { Pencil, Trash2, Plus, Check, X, Tag } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────
interface OrderTag {
  id: number
  name: string
  color: string
}

// ─── API helpers ──────────────────────────────────────────────────────────────
const fetchTags = () => api.get('/admin/order-tags').then(r => r.data.data as OrderTag[])
const addTag = (data: { name: string; color: string }) => api.post('/admin/order-tags', data)
const editTag = ({ id, name, color }: { id: number; name: string; color: string }) =>
  api.patch(`/admin/order-tags/${id}`, { name, color })
const removeTag = (id: number) => api.delete(`/admin/order-tags/${id}`)

// ─── Main component ───────────────────────────────────────────────────────────
export default function OrderTags() {
  const qc = useQueryClient()

  const { data: tags = [], isLoading } = useQuery<OrderTag[]>({
    queryKey: ['order-tags'],
    queryFn: fetchTags,
  })

  // ── Add form state ──────────────────────────────────────────────────────────
  const [addName, setAddName] = useState('')
  const [addColor, setAddColor] = useState('#6366f1')
  const [addError, setAddError] = useState('')

  // ── Edit state ──────────────────────────────────────────────────────────────
  const [editId, setEditId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')
  const [editError, setEditError] = useState('')

  // ── Mutations ───────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: addTag,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-tags'] })
      setAddName('')
      setAddColor('#6366f1')
      setAddError('')
      toast.success('Tag created successfully!')
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || 'Failed to create tag.'
      setAddError(msg)
      toast.error(msg)
    },
  })

  const updateMutation = useMutation({
    mutationFn: editTag,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-tags'] })
      setEditId(null)
      setEditError('')
      toast.success('Tag updated successfully!')
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || 'Failed to update tag.'
      setEditError(msg)
      toast.error(msg)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: removeTag,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-tags'] })
      toast.success('Tag deleted.')
    },
    onError: () => toast.error('Failed to delete tag.'),
  })

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleAdd = () => {
    const name = addName.trim()
    if (!name) { setAddError('Tag name is required.'); return }
    if (name.length > 50) { setAddError('Max 50 characters allowed.'); return }
    setAddError('')
    createMutation.mutate({ name, color: addColor })
  }

  const startEdit = (tag: OrderTag) => {
    setEditId(tag.id)
    setEditName(tag.name)
    setEditColor(tag.color)
    setEditError('')
  }

  const handleUpdate = () => {
    const name = editName.trim()
    if (!name) { setEditError('Tag name is required.'); return }
    if (name.length > 50) { setEditError('Max 50 characters allowed.'); return }
    setEditError('')
    updateMutation.mutate({ id: editId!, name, color: editColor })
  }

  const handleDelete = (tag: OrderTag) => {
    if (!window.confirm(`Delete tag "${tag.name}"? This cannot be undone.`)) return
    deleteMutation.mutate(tag.id)
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">

        {/* ── Header ── */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <Tag size={16} className="text-gray-500 shrink-0" />
            <div>
              <h2 className="text-base font-semibold text-gray-900 leading-tight">Order Tags</h2>
              <p className="text-xs text-gray-400 mt-0.5 leading-tight">
                Predefined tags that customers can quickly apply to their orders.
              </p>
            </div>
          </div>
          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full shrink-0">
            {tags.length} {tags.length === 1 ? 'tag' : 'tags'}
          </span>
        </div>

        {/* ── Add New Tag form ── */}
        <div className="px-6 py-4 bg-[#FAFAFA] border-b border-[#E5E5E5]">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
            Add New Tag
          </p>

          <div className="flex items-center gap-2">
            {/* Colour picker — styled as a small square swatch */}
            <div className="relative shrink-0">
              <input
                id="tag-color-picker"
                type="color"
                value={addColor}
                onChange={e => setAddColor(e.target.value)}
                title="Pick tag colour"
                className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
              />
              <div
                className="w-9 h-9 rounded-md border border-gray-300 cursor-pointer"
                style={{ backgroundColor: addColor }}
              />
            </div>

            {/* Name input */}
            <Input
              id="add-tag-name"
              placeholder="Tag name (max 50 chars)"
              value={addName}
              maxLength={50}
              onChange={e => { setAddName(e.target.value); setAddError('') }}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              className="flex-1 h-9"
            />

            {/* Add button */}
            <Button
              id="add-tag-btn"
              onClick={handleAdd}
              disabled={createMutation.isPending}
              className="h-9 shrink-0 flex items-center gap-1.5 px-4"
            >
              <Plus size={14} strokeWidth={2} />
              {createMutation.isPending ? 'Adding…' : 'Add Tag'}
            </Button>
          </div>

          {addError && (
            <p className="text-xs text-red-500 mt-2">{addError}</p>
          )}
        </div>

        {/* ── Tags list ── */}
        <div className="divide-y divide-[#F3F3F3]">
          {isLoading ? (
            <div className="px-6 py-10 text-center text-sm text-gray-400">
              Loading tags…
            </div>
          ) : tags.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Tag size={28} className="mx-auto mb-2 text-gray-200" />
              <p className="text-sm text-gray-400">No tags yet. Add your first tag above.</p>
            </div>
          ) : (
            tags.map(tag => (
              <div key={tag.id} className="px-6 py-3 flex items-center gap-3 min-h-[52px] bg-white">
                {editId === tag.id ? (
                  /* ── Inline edit row ── */
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {/* Edit colour picker */}
                    <div className="relative shrink-0">
                      <input
                        type="color"
                        value={editColor}
                        onChange={e => setEditColor(e.target.value)}
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                      />
                      <div
                        className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                        style={{ backgroundColor: editColor }}
                      />
                    </div>

                    {/* Edit name input */}
                    <Input
                      value={editName}
                      maxLength={50}
                      onChange={e => { setEditName(e.target.value); setEditError('') }}
                      onKeyDown={e => e.key === 'Enter' && handleUpdate()}
                      autoFocus
                      className="flex-1 h-8 text-sm"
                    />

                    {editError && (
                      <p className="text-xs text-red-500 shrink-0">{editError}</p>
                    )}

                    {/* Save / Cancel */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditId(null); setEditError('') }}
                        className="h-8 w-8 p-0"
                        title="Cancel"
                      >
                        <X size={13} />
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleUpdate}
                        disabled={updateMutation.isPending}
                        className="h-8 w-8 p-0"
                        title="Save"
                      >
                        <Check size={13} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* ── Display row ── */
                  <>
                    {/* Colour swatch */}
                    <span
                      className="w-3 h-3 rounded-full shrink-0 border border-black/10"
                      style={{ backgroundColor: tag.color }}
                    />

                    {/* Tag name */}
                    <span className="flex-1 text-sm font-medium text-gray-800 truncate">
                      {tag.name}
                    </span>

                    {/* Actions */}
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit(tag)}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                        title="Edit tag"
                      >
                        <Pencil size={13} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(tag)}
                        disabled={deleteMutation.isPending}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        title="Delete tag"
                      >
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
